import React, { useMemo } from 'react';
import { useBetting } from '../../../context/BettingContext';
import { getSelectionId, getProxyImage } from '../../../utils/formatters';
import { getSportIcon, isMatchLive } from '../../../utils/sportsUtils';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LobbyLiveEvents = ({ liveEvents, activeSport, loading }) => {
    const { addSelection, betSlip } = useBetting();
    const navigate = useNavigate();

    const filteredEvents = useMemo(() => {
        if (!liveEvents) return [];
        if (activeSport === 'all') return liveEvents;
        return liveEvents.filter(e => e.sportName === activeSport);
    }, [liveEvents, activeSport]);

    const onMatchClick = (id) => navigate(`/match/${id}`);
    const onOddClick = (e, game, market, outcome) => {
        e.stopPropagation();
        addSelection(game, market, outcome);
    };

    if (loading && filteredEvents.length === 0) {
        return (
            <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 text-[#F59E0B] animate-spin" />
            </div>
        );
    }

    if (filteredEvents.length === 0) {
        return null; // Don't show section if empty, cleaner look
    }

    return (
        <div className="px-4 pb-12">
            {/* Header Exact Match */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-[#EF4444] shadow-[0_0_10px_rgba(239,68,68,0.5)] animate-pulse"></div>
                    <h3 className="text-sm font-black text-white uppercase tracking-widest leading-none">
                        PARTIDOS EN VIVO
                    </h3>
                </div>

                <div className="bg-[#EF4444]/10 border border-[#EF4444]/20 px-3 py-1 rounded-full flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#EF4444] animate-pulse"></div>
                    <span className="text-[9px] font-black text-[#EF4444] uppercase tracking-widest">
                        {filteredEvents.length} LIVE
                    </span>
                </div>
            </div>

            {/* Horizontal Scroll Container like screenshot */}
            <div className="flex overflow-x-auto gap-4 pb-4 -mx-4 px-4 scrollbar-hide snap-x">
                {filteredEvents.map((event) => {
                    const market = event.markets?.[0];
                    const Icon = getSportIcon(event);
                    const leagueName = event.league || 'Torneo Internacional';
                    const score = event.score ? event.score.split('-') : ['0', '0'];

                    return (
                        <div
                            key={event.id}
                            onClick={() => onMatchClick(event.id)}
                            className="snap-center shrink-0 w-[280px] md:w-[320px] bg-[#18181b] border border-white/5 rounded-[1.5rem] p-4 relative group cursor-pointer hover:border-[#F59E0B]/30 transition-all duration-300"
                        >
                            {/* Card Header */}
                            <div className="flex justify-between items-start mb-6">
                                <div className="flex flex-col gap-1">
                                    <div className="flex items-center gap-2 text-neutral-400 text-[10px] font-bold uppercase tracking-wider">
                                        <Icon size={12} className="text-[#F59E0B]" />
                                        <span>{event.sportName || 'Deporte'}</span>
                                    </div>
                                    <span className="text-white font-bold text-xs uppercase tracking-tight truncate max-w-[180px]">
                                        {leagueName}
                                    </span>
                                </div>
                                <div className="px-2 py-1 rounded-lg border border-[#EF4444]/20 bg-[#EF4444]/10">
                                    <span className="text-[8px] font-black text-[#EF4444] uppercase tracking-widest flex items-center gap-1">
                                        <span className="w-1 h-1 bg-[#EF4444] rounded-full animate-pulse"></span>
                                        EN VIVO
                                    </span>
                                </div>
                            </div>

                            {/* Teams & Score */}
                            <div className="flex justify-between items-center mb-6 px-2">
                                {/* Team 1 */}
                                <div className="flex flex-col items-center gap-3 w-20">
                                    <div className="w-12 h-12 rounded-full bg-[#09090b] flex items-center justify-center p-2 border border-white/5 shadow-lg group-hover:scale-110 transition-transform">
                                        {event.team1Logo ? (
                                            <img src={getProxyImage(event.team1Logo)} alt={event.team1} loading="lazy" className="w-full h-full object-contain" onError={(e) => e.target.style.display = 'none'} />
                                        ) : (
                                            <span className="text-neutral-600 font-bold text-lg">{event.team1.charAt(0)}</span>
                                        )}
                                    </div>
                                    <span className="text-[10px] font-bold text-neutral-300 uppercase text-center leading-tight line-clamp-2 w-full">
                                        {event.team1}
                                    </span>
                                </div>

                                {/* VS / Score */}
                                <div className="flex flex-col items-center gap-1">
                                    <div className="text-2xl font-black text-white font-mono tracking-tighter flex items-center gap-1 bg-[#09090b] px-3 py-1 rounded-lg border border-white/5">
                                        {score[0] === '0' && score[1] === '0' ? (
                                            <span className="text-[#F59E0B] italic text-xl">VS</span>
                                        ) : (
                                            <>
                                                <span className="text-[#F59E0B]">{score[0]}</span>
                                                <span className="text-neutral-600 text-sm">:</span>
                                                <span className="text-[#F59E0B]">{score[1]}</span>
                                            </>
                                        )}
                                    </div>
                                    <span className="text-[9px] font-bold text-neutral-500 uppercase tracking-widest">
                                        {event.timer ? `${event.timer}'` : '1T'}
                                    </span>
                                </div>

                                {/* Team 2 */}
                                <div className="flex flex-col items-center gap-3 w-20">
                                    <div className="w-12 h-12 rounded-full bg-[#09090b] flex items-center justify-center p-2 border border-white/5 shadow-lg group-hover:scale-110 transition-transform">
                                        {event.team2Logo ? (
                                            <img src={getProxyImage(event.team2Logo)} alt={event.team2} loading="lazy" className="w-full h-full object-contain" onError={(e) => e.target.style.display = 'none'} />
                                        ) : (
                                            <span className="text-neutral-600 font-bold text-lg">{event.team2.charAt(0)}</span>
                                        )}
                                    </div>
                                    <span className="text-[10px] font-bold text-neutral-300 uppercase text-center leading-tight line-clamp-2 w-full">
                                        {event.team2}
                                    </span>
                                </div>
                            </div>

                            {/* Markets (1X2) */}
                            <div className="grid grid-cols-3 gap-2">
                                {['1', 'X', '2'].map((label) => {
                                    const outcome = market?.outcomes?.find(o => {
                                        const oName = o.name?.toLowerCase() || "";
                                        const t1 = event.team1?.toLowerCase() || "___";
                                        const t2 = event.team2?.toLowerCase() || "___";

                                        if (label === '1') return oName === 'home' || o.id === '1' || oName === t1 || (t1.length > 3 && oName.includes(t1));
                                        if (label === '2') return oName === 'away' || o.id === '2' || oName === t2 || (t2.length > 3 && oName.includes(t2));
                                        return oName === 'draw' || oName === 'x' || oName === 'empate' || o.id === 'x' || o.id === 'X';
                                    });
                                    const odd = outcome?.odds;
                                    const selId = market ? getSelectionId(event.id, market, outcome) : null;
                                    const isActive = selId ? betSlip.has(selId) : false;

                                    return (
                                        <button
                                            key={label}
                                            onClick={(e) => odd && onOddClick(e, event, market, outcome)}
                                            disabled={!odd}
                                            className={`
                                                h-9 rounded-lg flex flex-col items-center justify-center transition-all duration-200
                                                ${isActive
                                                    ? 'bg-[#F59E0B] text-black font-black'
                                                    : odd
                                                        ? 'bg-[#27272a] hover:bg-[#3f3f46] text-white '
                                                        : 'bg-[#27272a] opacity-50 cursor-not-allowed'}
                                            `}
                                        >
                                            <span className={`text-[8px] font-bold uppercase mb-px ${isActive ? 'text-black/70' : 'text-neutral-500'}`}>{label === 'X' ? 'Empate' : label === '1' ? 'Local' : 'Visita'}</span>
                                            <span className="text-xs font-bold font-mono">{odd ? odd.toFixed(2) : '-'}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default LobbyLiveEvents;
